import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { LOGO_URL, SERVICES, PHONE, PHONE_DISPLAY, INKS } from "@/components/brand";

const links = [
  { label: "Corporate gifting", to: "/corporate-gifting" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const close = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setServicesOpen(false);
    };
    const esc = (e) => e.key === "Escape" && setServicesOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, []);

  // Close menus and honour #hash after route changes
  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
    if (location.hash) {
      setTimeout(() => {
        const el = document.querySelector(location.hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  const onServices = location.pathname.startsWith("/services") || location.pathname.startsWith("/categories");
  const linkCls = ({ isActive }) =>
    `font-display font-medium text-[0.95rem] px-1 py-2 border-b-2 transition-colors ${isActive ? "border-process-magenta text-ink" : "border-transparent text-ink/75 hover:text-ink hover:border-ink"}`;

  return (
    <nav
      data-testid="navbar"
      aria-label="Main"
      className={`fixed top-0 inset-x-0 z-50 bg-paper/95 backdrop-blur-sm transition-[border-color] ${scrolled ? "border-b border-ink" : "border-b border-transparent"}`}
    >
      <div className="mx-auto max-w-page px-4 sm:px-6 lg:px-10 h-[4.5rem] flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0" aria-label="Print Xpress home">
          <img src={LOGO_URL} alt="Print Xpress" className="h-9 w-auto" />
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setServicesOpen(!servicesOpen)}
              aria-expanded={servicesOpen}
              data-testid="nav-link-services"
              className={`inline-flex items-center gap-1 font-display font-medium text-[0.95rem] px-1 py-2 border-b-2 transition-colors ${onServices ? "border-process-magenta text-ink" : "border-transparent text-ink/75 hover:text-ink hover:border-ink"}`}
            >
              What we print
              <ChevronDown size={15} aria-hidden="true" className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
            </button>
            {servicesOpen && (
              <div data-testid="services-dropdown"
                className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[min(44rem,90vw)] bg-stock border border-ink shadow-[6px_6px_0_0_#161616] p-6">
                <ul className="grid grid-cols-3 gap-x-6 gap-y-0.5">
                  {SERVICES.map((s, i) => (
                    <li key={s.slug}>
                      <Link to={`/services/${s.slug}`} data-testid={`service-link-${s.slug}`}
                        className="flex items-center gap-2.5 py-2 text-[0.95rem] text-ink hover:text-process-magenta transition-colors">
                        <span className="w-2 h-2 shrink-0" style={{ backgroundColor: INKS[i % 4] }} aria-hidden="true" />
                        {s.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkCls}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-5">
          <a href={`tel:+${PHONE}`} data-testid="nav-support-phone" className="font-display font-medium text-[0.95rem] text-ink hover:text-process-magenta transition-colors">
            {PHONE_DISPLAY}
          </a>
          <Link to="/contact#quote" className="btn btn-ink !py-3 !px-5" data-testid="nav-quote">Get a quote</Link>
        </div>

        <button
          type="button"
          data-testid="mobile-menu-toggle"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          className="lg:hidden -mr-2 p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-paper border-t border-ink max-h-[calc(100vh-4.5rem)] overflow-y-auto">
          <div className="px-4 sm:px-6 py-6 space-y-8">
            <div>
              <p className="font-display font-semibold text-graphite text-sm mb-3">What we print</p>
              <ul className="grid grid-cols-2 gap-x-4">
                {SERVICES.map((s, i) => (
                  <li key={s.slug}>
                    <Link to={`/services/${s.slug}`} className="flex items-center gap-2.5 py-2 text-ink">
                      <span className="w-2 h-2 shrink-0" style={{ backgroundColor: INKS[i % 4] }} aria-hidden="true" />
                      {s.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <ul className="border-t border-ink">
              {[{ label: "Home", to: "/" }, ...links].map((l) => (
                <li key={l.to} className="border-b border-rule">
                  <Link to={l.to} className="block py-3.5 font-display font-semibold text-xl text-ink">{l.label}</Link>
                </li>
              ))}
            </ul>
            <div className="flex flex-col gap-3">
              <Link to="/contact#quote" className="btn btn-magenta">Get a quote</Link>
              <a href={`tel:+${PHONE}`} className="btn btn-line">Call {PHONE_DISPLAY}</a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
